import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { SiteNav } from './SiteNav'

export function BlogLayout({ title, description, date, readTime, url, author, relatedPosts, children }) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description,
      datePublished: date,
      dateModified: date,
      author: {
        '@type': 'Person',
        name: author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'arisk.ai',
        url: 'https://arisk.ai',
      },
      url,
      mainEntityOfPage: url,
    }
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify(schema)
    document.head.appendChild(script)
    return () => document.head.removeChild(script)
  }, [title, description, date, url])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 text-gray-800 dark:text-neutral-200">
      <SiteNav />

      <article className="max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <header className="mb-10">
          <Link
            to="/blog"
            className="text-xs text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300 transition-colors mb-6 inline-block"
          >
            ← All posts
          </Link>
          <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-neutral-500 mb-4">
            <time dateTime={date}>{date}</time>
            <span>·</span>
            <span>{readTime}</span>
            <span>·</span>
            <span>By {author}</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white leading-snug mb-4">
            {title}
          </h1>
          <p className="text-base text-gray-500 dark:text-neutral-400 leading-relaxed">
            {description}
          </p>
        </header>

        {/* Body */}
        <div className="prose-content space-y-6 text-sm text-gray-700 dark:text-neutral-300 leading-relaxed">
          {children}
        </div>

        {/* Related posts */}
        {relatedPosts?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-neutral-700">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-neutral-500 mb-4">
              Related
            </h2>
            <div className="space-y-3">
              {relatedPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="block text-sm text-gray-700 dark:text-neutral-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {post.title} →
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 p-6 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-xl text-center">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            Redact sensitive text before sharing with AI
          </p>
          <p className="text-xs text-gray-500 dark:text-neutral-400 mb-4">
            Free, no account required. Everything runs in your browser.
          </p>
          <Link
            to="/"
            className="inline-block px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Try arisk.ai
          </Link>
        </div>
      </article>
    </div>
  )
}
